import { ResidentData, SortField, SortDirection, DisplayRow } from "../types/dashboard";

export const DATE_FIELDS: SortField[] = ["dateOfBirth", "admissionDate", "ardDate", "dueDate", "wdsr02", "caaCompletion", "carePlanCompletionDate", "transmissionDate", "requiredTransmissionDate", "createdAt"];

export const getStatusVariant = (status: string) => {
  switch (status) {
    case "Late": return "destructive";
    case "In Progress": return "default";
    case "Completed": return "outline";
    default: return "secondary";
  }
};

export const sortData = (
  data: ResidentData[],
  sortField: SortField | null,
  sortDirection: SortDirection
): ResidentData[] => {
  if (!sortField || !sortDirection) return data;

  return [...data].sort((a, b) => {
    const aValue = (a as unknown as Record<string, string>)[sortField] || "";
    const bValue = (b as unknown as Record<string, string>)[sortField] || "";

    // Handle date sorting
    if (DATE_FIELDS.includes(sortField)) {
      const aDate = aValue ? new Date(aValue).getTime() : 0;
      const bDate = bValue ? new Date(bValue).getTime() : 0;
      return sortDirection === "asc" ? aDate - bDate : bDate - aDate;
    }

    // Handle string sorting
    const comparison = String(aValue).localeCompare(String(bValue));
    return sortDirection === "asc" ? comparison : -comparison;
  });
};

export const addWeekSeparators = (data: ResidentData[]): DisplayRow[] => {
  const withSeparators: DisplayRow[] = [];

  // Always pin late items to the top
  const lateItems = data.filter((row: ResidentData) => row.status === "Late");
  const nonLateItems = data.filter((row: ResidentData) => row.status !== "Late");

  // Add Late section if there are late items
  if (lateItems.length > 0) {
    withSeparators.push({
      isCategorySeparator: true,
      categoryName: "Late Assessments",
      isLateSection: true
    });
    withSeparators.push(...lateItems.map(item => ({ ...item, isInLateSection: true })));
  }

  // Separate items with and without week numbers
  const itemsWithWeek = nonLateItems.filter((row: ResidentData) => typeof row.week === "number");
  const itemsWithoutWeek = nonLateItems.filter((row: ResidentData) => typeof row.week !== "number");

  // Process each week separately for items with week numbers
  const weeks = Array.from(new Set(itemsWithWeek.map(row => row.week!))).sort();

  weeks.forEach(week => {
    const weekItems = itemsWithWeek.filter((row: ResidentData) => row.week === week);

    if (weekItems.length === 0) return;

    // Add week separator
    withSeparators.push({
      isSeparator: true,
      weekNumber: week
    });

    // Add all week items
    withSeparators.push(...weekItems);
  });

  // Add items without week numbers at the end (no separator needed for these)
  if (itemsWithoutWeek.length > 0) {
    withSeparators.push(...itemsWithoutWeek);
  }

  return withSeparators;
};

export const addCategorySeparators = (data: ResidentData[]): DisplayRow[] => {
  const withSeparators: DisplayRow[] = [];

  // Always pin late items to the top
  const lateItems = data.filter((row: ResidentData) => row.status === "Late");
  const nonLateItems = data.filter((row: ResidentData) => row.status !== "Late");

  // Add Late section if there are late items
  if (lateItems.length > 0) {
    withSeparators.push({
      isCategorySeparator: true,
      categoryName: "Late Assessments",
      isLateSection: true
    });
    withSeparators.push(...lateItems.map(item => ({ ...item, isInLateSection: true })));
  }

  // Add non-late items
  withSeparators.push(...nonLateItems);

  return withSeparators;
};

// Calculate days until projected completion date
export const getDaysUntilCompletion = (dateString: string) => {
  if (!dateString) return null;
  const today = new Date();
  const completionDate = new Date(dateString);
  const diffTime = completionDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Get color variant for projected completion date pill
export const getCompletionDateVariant = (dateString: string, status: string) => {
  if (status === "Completed") return "outline"; // Neutral for completed

  const daysUntil = getDaysUntilCompletion(dateString);
  if (daysUntil === null) return "outline";

  if (daysUntil >= 7) return "success"; // Green - 7+ days
  if (daysUntil >= 4) return "warning"; // Orange - 4-6 days
  return "destructive"; // Red - 0-3 days
};

// Helper to format date as YYYY-MM-DD in local timezone
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Convert custom date range to backend format
// Supports empty start or end dates for open-ended ranges
export const convertCustomDateRangeToFilter = (
  startDate: string | null,
  endDate: string | null
): string | null => {
  if (!startDate && !endDate) return null;

  // Format: "startDate,endDate"
  // Empty start: ",2026-12-31" (all items up to end date)
  // Empty end: "2026-01-01," (all items from start date onwards)
  // Both: "2026-01-01,2026-12-31" (items between dates)
  return `${startDate || ""},${endDate || ""}`;
};

// Get default filter for page load: all items up to 7 days from now (includes overdue)
// This is the recommended default filter - shows urgent work that needs attention
export const getDefaultDueDateFilter = (): string => {
  const today = new Date();
  const endDate = new Date(today);
  endDate.setDate(today.getDate() + 7);

  // Use custom format with empty start to include past due items
  return convertCustomDateRangeToFilter(null, formatDateLocal(endDate)) || "";
};

// Convert time range label to date range based on user's local timezone
// Returns { startDate, endDate } as YYYY-MM-DD strings
export const convertTimeRangeToDateRange = (label: string): { startDate: string; endDate: string } | null => {
  if (!label || label.trim() === "") return null;

  const today = new Date();
  let startDate = today;
  let endDate = today;

  switch (label) {
    case "Today":
      // Today only
      startDate = today;
      endDate = today;
      break;
    case "Next Day":
      // Tomorrow only
      startDate = new Date(today);
      startDate.setDate(today.getDate() + 1);
      endDate = new Date(startDate);
      break;
    case "Next 3 Days":
      // Today through 3 days from now
      startDate = today;
      endDate = new Date(today);
      endDate.setDate(today.getDate() + 3);
      break;
    case "Next 7 Days":
      // Today through 7 days from now (upcoming only, no past due)
      startDate = today;
      endDate = new Date(today);
      endDate.setDate(today.getDate() + 7);
      break;
    case "Next 14 Days":
      startDate = today;
      endDate = new Date(today);
      endDate.setDate(today.getDate() + 14);
      break;
    case "Next 30 Days":
      startDate = today;
      endDate = new Date(today);
      endDate.setDate(today.getDate() + 30);
      break;
    case "Next 60 Days":
      startDate = today;
      endDate = new Date(today);
      endDate.setDate(today.getDate() + 60);
      break;
    case "60+ Days": {
      // Due more than 60 days out: open-ended range starting the day AFTER the
      // "Next 60 Days" window (today..today+60) ends, so the two presets
      // partition the future without overlapping. Empty endDate => no upper
      // bound; mapFiltersToParams sends this as the "start," open-ended format,
      // which the backend reads as due_date >= start.
      const farStart = new Date(today);
      farStart.setDate(today.getDate() + 61);
      return { startDate: formatDateLocal(farStart), endDate: "" };
    }
    case "Past Due":
      // All overdue items (due before today)
      startDate = new Date("2000-01-01");  // Far past date
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);  // Yesterday
      break;
    default:
      // Unknown label, return null
      return null;
  }

  return {
    startDate: formatDateLocal(startDate),
    endDate: formatDateLocal(endDate),
  };
};

// ---------------------------------------------------------------------------
// Due-date filter presentation helpers (NETSOL-11598)
//
// The dueDate filter value is polymorphic: "" (none), "no_due_date", a preset
// label ("Next 7 Days", "Past Due", ...), or a "startDate,endDate" range
// (custom or the on-load default; either side may be empty for open-ended).
// These helpers are intentionally Atlas-free so they stay cheap to import and
// easy to unit test. CalendarDate <-> range-string conversion (which needs the
// Atlas date utils) lives with the DateRangeFilter component.
// ---------------------------------------------------------------------------

export interface DueDatePreset {
  /** Value stored in FilterState.dueDate (preset label or "no_due_date"). */
  value: string;
  /** Human-friendly label shown in the preset rail. */
  label: string;
}

// Preset shortcuts shown in the DateRangeFilter rail. Mirrors the old
// FilterDropdown "Time Range" options minus "Custom Range..." (the calendar
// now owns custom ranges). Order: overdue, then increasing horizons, then the
// no-due-date sentinel.
export const DUE_DATE_PRESETS: DueDatePreset[] = [
  { value: "Past Due", label: "Past due" },
  { value: "Today", label: "Today" },
  { value: "Next Day", label: "Next day" },
  { value: "Next 3 Days", label: "Next 3 days" },
  { value: "Next 7 Days", label: "Next 7 days" },
  { value: "Next 14 Days", label: "Next 14 days" },
  { value: "Next 30 Days", label: "Next 30 days" },
  { value: "Next 60 Days", label: "Next 60 days" },
  { value: "60+ Days", label: "60+ days" },
  { value: "no_due_date", label: "No due date" },
];

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Format a "YYYY-MM-DD" string as "Mon D, YYYY" without going through the
// Date constructor (which would interpret the string as UTC midnight and can
// shift the day in negative-offset timezones).
const formatYmd = (ymd: string): string => {
  const parts = ymd.split("-");
  if (parts.length !== 3) return ymd;
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!year || !month || !day || month < 1 || month > 12) return ymd;
  return `${MONTHS_SHORT[month - 1]} ${day}, ${year}`;
};

// Human-friendly label for the current dueDate filter value, used by both the
// DateRangeFilter trigger button and the active-filter chip.
export const formatDueDateLabel = (value: string): string => {
  const v = (value ?? "").trim();
  if (v === "") return "All due dates";
  if (v === "no_due_date") return "No due date";

  if (v.includes(",")) {
    const [rawStart, rawEnd] = v.split(",");
    const start = rawStart.trim();
    const end = rawEnd?.trim() ?? "";
    if (start && end) return `${formatYmd(start)} – ${formatYmd(end)}`;
    if (start) return `From ${formatYmd(start)}`;
    if (end) return `Through ${formatYmd(end)}`;
    return "All due dates";
  }

  // Preset label (already human-friendly).
  return v;
};
